import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Play, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
	createProfessionalTutorialDemoAccessState,
	TUTORIAL_DEMO_ENTRY_QUICK_DARKEN_MS,
} from '@/constants/professionalTutorialDemo';
import {
	mountTutorialDemoEntryBlackOverlay,
	TUTORIAL_DEMO_ENTRY_OVERLAY_ID,
} from '@/pages/professional/tutorial-demonstracao/tutorialDemonstracaoEntryOverlay';
import { MediaLightbox } from '@/pages/public/landing/components/MediaLightbox';
import { ProntuarioProfileAccordionBlock } from '@/pages/professional/sessao-chamada/components/prontuario/ProntuarioProfileAccordionBlock';

const TUTORIAL_MODAL_SCROLL_CLASS = cn(
	'overflow-y-auto overscroll-contain scroll-smooth',
	'[scrollbar-width:thin] [scrollbar-color:theme(colors.stone.300)_transparent]',
	'[&::-webkit-scrollbar]:w-1.5',
	'[&::-webkit-scrollbar-track]:bg-transparent',
	'[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-stone-300',
	'hover:[&::-webkit-scrollbar-thumb]:bg-stone-400',
);

const TUTORIAL_CONTENT_MAX_HEIGHT_CLASS = 'max-h-[calc(88vh-10rem)]';

const PRONTUARIO_TUTORIAL_IMAGES = {
	sessionWithProntuario:
		'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1781539952/Sessao1_dgwtte.png',
	sessionReview:
		'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1781540500/Sessao2_lcfutb.png',
} as const;

type TutorialStep = {
	imageSrc: string;
	imageAlt: string;
	title?: string;
	text: string;
	reverseOnDesktop?: boolean;
};

type TutorialItem = {
	id: string;
	title: string;
	description: string;
	steps: TutorialStep[];
};

const TUTORIALS: TutorialItem[] = [
	{
		id: 'videochamada-prontuario',
		title: 'Videochamada e Prontuário',
		description:
			'Conduza consultas online completas enquanto a IA resolve toda a documentação por você. Curioso como fica na prática? Clique em "Ver demonstração" logo ao lado e faça uma chamada teste para ver o funcionamento.',
		steps: [
			{
				imageSrc: PRONTUARIO_TUTORIAL_IMAGES.sessionWithProntuario,
				imageAlt:
					'Tela da videochamada com o painel do prontuário aberto',
				title: '1. Acesse tudo durante a consulta',
				text: 'Ao entrar na reunião, o prontuário fica disponível ao lado da videochamada, pronto para abrir quando precisar. Consulte o perfil do paciente, o histórico clínico e sessões anteriores sem sair da tela. Faça anotações livres enquanto conversa — tudo que você precisa está ali, sem alternar entre abas ou janelas.',
			},
			{
				imageSrc: PRONTUARIO_TUTORIAL_IMAGES.sessionReview,
				imageAlt:
					'Tela de revisão do prontuário preenchido automaticamente',
				title: '2. Prontuário preenchido pela IA',
				text: 'Encerrou a chamada? A IA processa o atendimento e preenche o prontuário automaticamente. Você recebe um resumo completo da sessão, edita o que quiser e confirma quando estiver satisfeito. Tudo fica salvo no perfil do paciente e acessível a qualquer momento — dentro ou fora da sessão.',
				reverseOnDesktop: true,
			},
		],
	},
];

type LightboxMedia = {
	src: string;
	alt: string;
};

type Props = {
	isOpen: boolean;
	onClose: () => void;
	initialActiveTutorialId?: string | null;
};

const ProfessionalTutorialsModal = ({
	isOpen,
	onClose,
	initialActiveTutorialId = null,
}: Props) => {
	const navigate = useNavigate();
	const [activeTutorialId, setActiveTutorialId] = useState<string | null>(
		null,
	);
	const [lightboxMedia, setLightboxMedia] = useState<LightboxMedia | null>(
		null,
	);

	useEffect(() => {
		if (!isOpen) {
			setActiveTutorialId(null);
			return;
		}

		// Por padrão, o tutorial "Videochamada e Prontuário" deve abrir expandido.
		// Quando `initialActiveTutorialId` vier do retorno de navegação, respeitamos ele.
		setActiveTutorialId(
			initialActiveTutorialId ?? TUTORIALS[0]?.id ?? null,
		);
	}, [initialActiveTutorialId, isOpen]);

	const handleOpenDemonstracao = useCallback(() => {
		mountTutorialDemoEntryBlackOverlay({ animateIn: true });

		window.setTimeout(() => {
			navigate('/profissional/tutorial/demonstracao', {
				state: createProfessionalTutorialDemoAccessState({
					fromPlayTransition: true,
				}),
			});
			onClose();
		}, TUTORIAL_DEMO_ENTRY_QUICK_DARKEN_MS);
	}, [navigate, onClose]);

	useEffect(() => {
		if (!isOpen) {
			setActiveTutorialId(null);
			setLightboxMedia(null);
			if (!document.getElementById(TUTORIAL_DEMO_ENTRY_OVERLAY_ID)) {
				document.body.style.overflow = 'auto';
			}
			return;
		}

		document.body.style.overflow = 'hidden';
		return () => {
			if (!document.getElementById(TUTORIAL_DEMO_ENTRY_OVERLAY_ID)) {
				document.body.style.overflow = 'auto';
			}
		};
	}, [isOpen]);

	const handleToggleTutorial = (tutorialId: string) => {
		setActiveTutorialId((current) =>
			current === tutorialId ? null : tutorialId,
		);
	};

	if (!isOpen) return null;

	return createPortal(
		<>
			<div className="fixed inset-0 z-[10020] flex items-center justify-center p-4">
				<div
					className="fixed inset-0 bg-black/50"
					onClick={onClose}
					aria-hidden
				/>

				<div className="relative z-10 flex h-[88vh] w-[72vw] max-w-[1000px] flex-col overflow-hidden scroll-smooth rounded-2xl bg-white shadow-2xl">
					<div className="flex shrink-0 items-center justify-between border-b border-stone-200 px-6 py-4">
						<h2 className="text-[17px] font-semibold text-stone-950">
							Tutorial
						</h2>
						<button
							type="button"
							onClick={onClose}
							className="flex size-9 items-center justify-center rounded-full bg-stone-100 text-stone-600 transition-colors hover:bg-stone-200 hover:text-stone-900"
							aria-label="Fechar"
						>
							<X size={18} />
						</button>
					</div>

					<div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
						{TUTORIALS.map((tutorial) => {
							const isActive = activeTutorialId === tutorial.id;

							return (
								<div key={tutorial.id} className="w-full">
									<ProntuarioProfileAccordionBlock
										label={tutorial.title}
										variant="light"
										isActive={isActive}
										onClick={() =>
											handleToggleTutorial(tutorial.id)
										}
										contentMaxHeightClass={
											TUTORIAL_CONTENT_MAX_HEIGHT_CLASS
										}
										contentScrollClass={
											TUTORIAL_MODAL_SCROLL_CLASS
										}
										fillAvailable={isActive}
									>
										<div className="flex flex-col gap-8">
											<div className="flex items-start justify-between gap-4">
												<p className="min-w-0 flex-1 text-sm leading-relaxed text-stone-600">
													{tutorial.description}
												</p>
												<button
													type="button"
													onClick={
														handleOpenDemonstracao
													}
													className="flex shrink-0 items-center gap-2 rounded-xl border-2 border-brand-purple/70 bg-white px-4 py-2.5 text-sm font-semibold text-stone-800 transition-colors hover:border-brand-purple hover:bg-stone-100 focus:outline-none"
												>
													<Play
														className="size-4 shrink-0 fill-current text-brand-purple"
														aria-hidden
													/>
													Ver demonstração
												</button>
											</div>
										</div>
									</ProntuarioProfileAccordionBlock>
								</div>
							);
						})}
					</div>
				</div>
			</div>

			<MediaLightbox
				isOpen={lightboxMedia !== null}
				onClose={() => setLightboxMedia(null)}
				src={lightboxMedia?.src ?? ''}
				alt={lightboxMedia?.alt}
				className="z-[10030]"
			/>
		</>,
		document.body,
	);
};

export { ProfessionalTutorialsModal };
